// IMPROVEME(TEA-241)

import { utcStringToDisplayString } from '@thyme/libs/src/date/formatting'
import {
  CitedContextDatapoint,
  ValidatedQuote,
} from '@/libs/features/llm/types'

/**
 * Formats the input text by replacing underscores with spaces and capitalizing each word.
 * If text already had spaces, return as is.
 * @param s
 */
function humanizeQuote(s: string): string {
  if (!s) return s

  // If value is a valid datetime, format it nicely and return it
  const dateFormatted = formatDate(s)
  if (dateFormatted !== null) {
    return dateFormatted
  }

  // If there are colons, this is likely a key-value pair, so humanize each part separately
  if (s.includes(':'))
    return s
      .split(':')
      .map((p) => humanizeQuote(p.trim()))
      .join(': ')

  // If value already looks like human text (contains spaces and lower case), pass through
  if (/[a-z]/.test(s) && s.includes(' ')) return '"' + String(s) + '"'

  const cleaned = String(s)
    .replaceAll('_', ' ')
    .toLowerCase()
    .replaceAll(/\b\w/g, (c) => c.toUpperCase())
  return cleaned
}

/**
 * Formats a date string into a more readable format.
 * If the input string is not a valid date, returns null.
 * @param s
 */
export function formatDate(s: string) {
  const result = utcStringToDisplayString(s, false)
  return result === 'Invalid date' ? null : result
}

/**
 *
 * @param source
 */
export function formatSourceHeader(source: CitedContextDatapoint) {
  let header = humanizeQuote(source.sourceType)

  // use title if available, else subtype
  if (source.displayTitle) {
    header += `: ${source.displayTitle}`
  } else if (source.sourceSubtype) {
    header += `: ${humanizeQuote(source.sourceSubtype)}`
  }

  if (source.displayTimestamp) {
    const formattedDate = formatDate(source.displayTimestamp)
    header += ' on ' + (formattedDate ? formattedDate : source.displayTimestamp)
  }

  return header
}

export interface FormattedQuote {
  label: string
  content: string
}

/**
 *
 * @param quotes
 */
export function formatQuotes(quotes: ValidatedQuote[]): FormattedQuote[] {
  return quotes.map((q) => ({
    label: humanizeQuote(q.fieldName),
    content: humanizeQuote(q.quote),
  }))
}
